import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';

import Modal from '@/components/Modal/Modal';
import useModalState from '@/hooks/pure/useModalState';
import useStates from '@/hooks/pure/useStates';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { productInfoService } from '../api';
import { BaseTable, type RowData, type TableColumnT } from '../components/BaseTable';
import { ProductManagerEllipsisMenu } from '../components/DeleteItem/ProductManagerEllipsisMenu';
import { DetailModal } from '../components/DetailModal/DetailModal';
import { EllipsisCell } from '../components/EllipsisCell/EllipsisCell';
import { ListHeader } from '../components/ListHeader';
import { MergeModal } from '../components/MergeModal';
import useProductStore from '../store/ProductStore';
import type { ProductT } from '../types';

export const BrandView = () => {
  const fetchProductList = useProductStore((state) => state.fetchProductList);
  const brandMap = useProductStore((state) => state.brandMap);

  const brandOptions = useMemo(() => {
    return Array.from(brandMap.values());
  }, [brandMap]);

  const [checkedList, setCheckedList] = useState<string[]>([]);
  const mergeModal = useModalState();

  const { state, setValue } = useStates({
    searchValue: '',
  });

  const filteredBrandList = useMemo(() => {
    return brandOptions.filter((brand) => {
      if (
        state.searchValue &&
        !brand.name?.toLowerCase().includes(state.searchValue.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [brandOptions, state.searchValue]);

  return (
    <div className="flex flex-col gap-4 flex-1 overflow-auto">
      <ListHeader
        mergeDisabled={checkedList.length < 2}
        onSearchSubmit={(value) => {
          setValue('searchValue', value);
        }}
        clearSearch={() => {
          setValue('searchValue', '');
        }}
        onMergeClick={() => {
          mergeModal.open();
        }}
      >
        {checkedList.length > 0 && (
          <span className="text-sm text-gray-600">
            {checkedList.length} selected
          </span>
        )}
      </ListHeader>
      <BrandTable
        searchValue={state.searchValue}
        brandList={filteredBrandList}
        checkedList={checkedList}
        setCheckedList={setCheckedList}
      />
      <Modal status={mergeModal.status} closeModal={mergeModal.close}>
        <MergeModal
          items={checkedList.map((id) => ({
            id,
            name: brandMap.get(id)?.name || '-',
          }))}
          target="brand"
          mergeCallback={async (targetId, list) => {
            await productInfoService.mergeBrands({
              from: list.filter((id) => id !== targetId),
              to: targetId,
            });
          }}
          onDoneCallback={() => {
            setCheckedList([]);
            fetchProductList(getCurrWorkspaceAlias());
          }}
        />
      </Modal>
    </div>
  );
};

const BrandTable = (props: {
  searchValue: string;
  brandList: ProductBrandOption[];
  checkedList: string[];
  setCheckedList: Dispatch<SetStateAction<string[]>>;
}) => {
  const detailModalState = useModalState<{
    alias: string | null;
  }>({
    alias: null,
  });
  const brandMap = useProductStore((state) => state.brandMap);
  const productMap = useProductStore((state) => state.groupedProductMap);
  const groupedProductMap = useMemo(() => {
    const map = new Map<string, ProductT[]>();
    brandMap.forEach((brand) => {
      const products = Array.from(productMap.values()).filter(
        (product) => product.brand_id === brand.id,
      );
      map.set(brand.id, products);
    });
    return map;
  }, [brandMap, productMap]);
  const { brandList, checkedList, searchValue, setCheckedList } = props;

  const BrandTableColumns: TableColumnT<ProductBrandOption>[] = [
    {
      columnKey: 'brand',
      Header: 'Brand',
      Cell: (rowData: RowData<ProductBrandOption>) => (
        <span className="truncate">{rowData.rowData.name}</span>
      ),
      className: 'flex-1 min-w-0',
      sortable: true,
      sort: (a, b) => {
        return a.name?.localeCompare(b.name || '') || 0;
      },
    },
    {
      columnKey: 'products',
      Header: 'Products',
      Cell: (rowData: RowData<ProductBrandOption>) => {
        const products = groupedProductMap.get(rowData.rowData.id);
        return (
          <EllipsisCell
            dataList={
              products?.map((product) => ({
                id: product.alias,
                name: product.product_name || '',
              })) || []
            }
            columnWidth={400}
          />
        );
      },
      className: 'w-100',
    },
    {
      columnKey: 'action',
      Header: 'Action',
      Cell: (rowData: RowData<ProductBrandOption>) => (
        <div className="flex justify-center w-full">
          <ProductManagerEllipsisMenu
            target="brand"
            targetId={rowData.rowData.id}
          />
        </div>
      ),
      className: 'w-20',
    },
  ];
  return (
    <>
      <BaseTable
        defaultSortKey="brand"
        columns={BrandTableColumns}
        data={brandList.map((brand) => ({
          rowKey: brand.id,
          rowData: brand,
        }))}
        searchValue={searchValue}
        onRowClick={(rowKey) =>
          detailModalState.open({ modalState: { alias: rowKey } })
        }
        placeholder={{
          title: 'No brand yet',
        }}
        useCheckbox
        checkedList={checkedList}
        setCheckedList={setCheckedList}
      />
      <Modal
        status={detailModalState.status}
        closeModal={detailModalState.close}
      >
        {detailModalState.modalState?.alias && (
          <DetailModal alias={detailModalState.modalState.alias} type="brand" />
        )}
      </Modal>
    </>
  );
};
